import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useField } from 'react-final-form';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useFields, { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import {
  required,
  startsWithCapital,
  validEmail,
  validBirthdate,
  validateNameValue,
  validRankingField
} from '@ubeya/shared/utils/form';
import {
  ComposeFields,
  DynamicFieldComp,
  Loader as BaseLoader,
  PhoneField,
  InputField,
  SelectField,
  TextInput as BaseTextInput,
  CheckBoxField,
  Header3SemiBold,
  AutoCompleteOff,
  Button as BaseButton,
  SmallBody,
  ToggleField,
  ContextMenu as BaseContextMenu,
  ExplanationTooltip,
  TextInput,
  Accordion
} from '@ubeya/shared-web/components';
import { media, responsiveHide } from '@ubeya/shared-web/style';
import { ReactComponent as PhoneMenuIcon } from '@ubeya/shared-web/assets/chat.svg';
import { ReactComponent as BasePhoneIcon } from '@ubeya/shared-web/assets/phone-big.svg';
import { ReactComponent as BaseChatIcon } from '@ubeya/shared-web/assets/ubeya-chat-fill.svg';
import { ReactComponent as BaseWhatsappIcon } from '@ubeya/shared-web/assets/whatsapp.svg';
import useEmploymentTypes from '@ubeya/shared/hooks/generic/useEmploymentTypes';
import { API_DATE_TIME_FORMAT, CREATE_USER_BY } from '@ubeya/shared/constants';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import useChatIframe from '@ubeya/shared-web/hooks/useChatIframe';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import useEmployee from '@ubeya/shared/hooks/employees/useEmployee';
import useEmployeePreferredAreas from '@ubeya/shared/hooks/employees/useEmployeePreferredAreas';
import {
  ALPHA_ACCOUNT_ID,
  FIRE_GROUP_ACCOUNT_ID,
  hasRestrictedFeatures,
  ONB_ACCOUNT_ID,
  DN_PRIVILAGED_ADMINS,
  LEVY_SUPER_ADMIN_IDS,
  DELAWARE_NORTH_ID,
  CH_CO_TRAINING_PLATFORM_ACCOUNT_ID,
  ARC_ACCOUNT_ID,
  SETUP_ACCOUNT_ID,
  FNB_ACCOUNT_ID,
  MADRID_LEGENDS_ACCOUNT_ID,
  isSeniorityModelPools,
  isSeniorityModelRankingBased
} from '@ubeya/shared/utils/accounts';
import moment from 'moment';
import { formatLevelOption } from '@ubeya/shared-web/utils/employee';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useGroupedBranchesOptions from 'shared/hooks/useGroupedBranchesOptions';
import PositionsSelectField from 'shared/components/AddNewSelectors/PositionsSelectField';
import DepartmentSelectField from 'shared/components/AddNewSelectors/DepartmentSelectField';
import { BlockedClientsSelectField, PreferredClientsSelectField } from 'shared/components/AddNewSelectors';
import { DaysFields } from '../../DayButton';
import EmployeeKpis from '../EmployeeKpis';
import { AccordionContent, RootContainer, RootWrapper } from '../styled';
import SensitiveFieldProfileMask from './SensitiveFieldProfileMask';
import FieldModal from 'settings/components/AccountSettings/Fields/FieldModal';
import {
  COMPASS_DEMO_ACCOUNT_AKA_ID,
  COMPASS_DEMO_ACCOUNT_ID,
  COMPASS_DEMO_ACCOUNT_NI_NUMBER_ID,
  COMPASS_DEMO_ACCOUNT_SECONDARY_ADDRESS_ID,
  ISRAEL_647_ACCOUNT_ID,
  MADRID_LEGENDS_CONTRACTED_BASE_RATE_FIELD_ID,
  MAX_FIRST_LAST_NAME_LENGTH,
  MINT_ACCOUNT_EMPLOYEE_TYPE_FIELD_ID,
  MINT_ACCOUNT_ID
} from 'employees/constants';

const EXTRACTED_DYNAMIC_FIELDS = [
  'employee-id',
  'gender',
  'level',
  'birthdate',
  'start-employment-date',
  'end-employment-date',
  'nickname',
  'seniority-ranking'
];

const menuIcon = css`
  height: 20px;
  width: 20px;
`;

const WhatsappIcon = styled(BaseWhatsappIcon)`
  ${menuIcon}
`;

const ChatIcon = styled(BaseChatIcon)`
  ${menuIcon};
  width: 22px;
`;

const PhoneIcon = styled(BasePhoneIcon)`
  ${menuIcon};
  stroke-width: 1;
`;

const PhoneMenuIconSmall = styled(PhoneMenuIcon)`
  ${menuIcon}
`;

const AgeText = styled(BaseTextInput)`
  display: flex;
  align-items: center;
  width: 100%;

  div {
    align-items: flex-start;
    width: 100%;
  }

  > input:disabled {
    color: inherit;
    text-align: center;
    border: none;
    width: 100%;
    border-radius: 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  }
`;

const AccuredText = styled(Header3SemiBold)`
  font-size: 14px;
  margin-top: 30px;
`;

const Category = styled(Header3SemiBold)`
  background: ${({ theme }) => theme.colors.grey1};
  letter-spacing: 2.25px;
  color: ${({ theme }) => theme.colors.black};
  margin: 40px -13px 10px;
  padding: 4px 13px;
`;

const HiddenFieldWrapper = styled.div`
  display: none;
`;

const MenuContainer = styled.div`
  padding: 12px 8px;
  background-color: ${({ theme }) => theme.colors.grey1};
  border-left: 1px ${({ theme }) => theme.colors.grey2} solid;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  position: absolute;
  bottom: 1px;
  ${({ isRtl }) => (isRtl ? 'left:0' : 'right:1px')};
`;

const ContextMenu = styled(BaseContextMenu).attrs(() => ({ className: 'employee-card-context-menu' }))`
  > div:last-child {
    > div {
      padding: 8px;
    }
  }
`;

const Notes = styled(InputField)`
  textarea {
    min-height: 60px !important;
  }
`;

const FieldsContainer = styled.div.attrs({ className: 'fields-container' })`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-gap: 16px;
  padding: 0 4px;

  ${media.tablet`
    grid-template-columns: repeat(${({ $count = 2 }) => $count}, minmax(0, 1fr));
    

    > ${Category} {
      grid-column: 1/-1;
    }

    > .span-2 {
      grid-column: span 2;
    }

    > .span-3 {
      grid-column: span 3;
    }

    > .span-4 {
      grid-column: span 4;
    }
  
  `}

  ${({ $shouldBrakeLabels }) =>
    $shouldBrakeLabels &&
    css`
      ${SmallBody} {
        white-space: break-spaces;
      }
    `}

  input[type='checkbox'] {
    position: absolute;
    width: 0;
    height: 0;
  }
`;

const EmployeeIdInputField = styled(InputField)`
  > div {
    align-items: flex-start;
  }
`;

const Loader = styled(BaseLoader)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const Button = styled(BaseButton).attrs(() => ({ secondary: true, type: 'button' }))`
  margin-top: 26px;
  padding: 9px 20px;
`;

const MobileKpis = styled.div`
  padding: 8px 4px 16px;
  ${responsiveHide.tablet}
`;

const Profile = ({ isLoading, addMode, userId, onClose, employeeId, userDevice }) => {
  const { t } = useContext(ConfigContext);
  const { branchesOptions } = useBranches();
  const { accountId, account } = useAccount();
  const { account: accountConfig } = useAccountConfig();
  const {
    isUbeyaAdmin,
    data: { id: adminId }
  } = useUser();
  const { fields, addField } = useFields();
  const {
    isRtl,
    disableEmployeeIdField: configDisableEmployeeIdField,
    enableContractFeature,
    enableContractLeaveManagementFeature,
    isEmployeePreferredAreasEnabled,
    organizationId
  } = useContext(ConfigContext);
  const canViewSensitiveData = !!useContext(PrivilegesContext)[privileges.SENSITIVE_DATA_ACCESS];

  const renderDynamicFieldOrMask = ({ baseField, props, label }) => {
    if (!baseField) return null;
    if (baseField.isSensitive && !canViewSensitiveData) {
      const key = props.field?.id ?? baseField.id;
      return (
        <React.Fragment key={key}>
          <SensitiveFieldProfileMask label={label ?? t(baseField.name)} />
          <HiddenFieldWrapper>
            <DynamicFieldComp {...props} />
          </HiddenFieldWrapper>
        </React.Fragment>
      );
    }
    const key = props.field?.id ?? baseField.id;
    return <DynamicFieldComp key={key} {...props} />;
  };

  const disableEmployeeIdField =
    // specific dn admins that should not be disabled
    !DN_PRIVILAGED_ADMINS.includes(adminId) &&
    // otherwise, use usual logic
    (configDisableEmployeeIdField ||
      (accountId !== DELAWARE_NORTH_ID &&
        hasRestrictedFeatures({ accountId, organizationId }) &&
        !LEVY_SUPER_ADMIN_IDS.includes(adminId)));

  const {
    input: { value: isDaily }
  } = useField('isDaily');
  const {
    input: { value: faceId }
  } = useField('faceId');
  const {
    input: { value: branches }
  } = useField('branches');
  const {
    input: { value: positions, onChange: changePositions }
  } = useField('positions');
  const {
    input: { value: primaryPositionIds, onChange: changePrimaryPositionIds }
  } = useField('primaryPositionIds');

  const { positions: allPositions, mappedPositions, filteredPositionsByAllowedBranchIds } = usePositions();

  const {
    groupedBranchesOptions,
    getOptionSpecificStyle: getBranchOptionStyle,
    filterFunc: branchFilterFunc,
    expandBranchIds
  } = useGroupedBranchesOptions({ isGroupSelectable: true, selectedBranchIds: branches });

  const handleBranchesChange = useCallback((value, input) => input.onChange(expandBranchIds(value)), [expandBranchIds]);

  const positionsOptionsWithDisabled = useMemo(() => {
    const inScopeIds = new Set(filteredPositionsByAllowedBranchIds.map(({ id }) => id));
    return allPositions.map(({ id, slug }) => ({ value: id, label: slug, isDisabled: !inScopeIds.has(id) }));
  }, [allPositions, filteredPositionsByAllowedBranchIds]);

  const primaryPositionsOptions = useMemo(
    () =>
      (positions || []).map((positionId) => ({
        value: positionId,
        label: mappedPositions?.[positionId]?.label ?? mappedPositions?.[positionId]?.slug ?? String(positionId)
      })),
    [positions, mappedPositions]
  );

  // Keep primaryPositionIds a subset of positions.
  useEffect(() => {
    if (!Array.isArray(primaryPositionIds) || primaryPositionIds.length === 0) {
      return;
    }
    const selected = new Set(positions || []);
    const filtered = primaryPositionIds.filter((id) => selected.has(id));
    if (filtered.length !== primaryPositionIds.length) {
      changePrimaryPositionIds(filtered);
    }
  }, [positions, primaryPositionIds, changePrimaryPositionIds]);

  const hasPositions = (positions || []).length > 0;

  const { preferredAreasOptions, isLoading: isLoadingPreferredAreas } = useEmployeePreferredAreas({
    employeeId,
    branchIds: branches,
    enabled: !!isEmployeePreferredAreasEnabled && !addMode
  });

  const {
    input: { value: preferredAreaIds, onChange: changePreferredAreas }
  } = useField('preferredAreaIds');

  // Keep preferredAreaIds a subset of the areas the selected branches reach. Without this, removing a
  // branch leaves ids that the Select cannot render (so the admin can't see or remove them) and the
  // save is then rejected by the backend validator with no visible cause. Skipped while the options
  // are still loading, so an in-flight request never wipes the selection.
  // Also skipped when the feature is off: the options query is disabled then, so an empty options list
  // would wipe a saved selection.
  useEffect(() => {
    if (
      !isEmployeePreferredAreasEnabled ||
      isLoadingPreferredAreas ||
      !Array.isArray(preferredAreaIds) ||
      preferredAreaIds.length === 0
    ) {
      return;
    }
    const available = new Set(preferredAreasOptions.map(({ value }) => value));
    const filtered = preferredAreaIds.filter((id) => available.has(id));
    if (filtered.length !== preferredAreaIds.length) {
      changePreferredAreas(filtered);
    }
  }, [
    isEmployeePreferredAreasEnabled,
    preferredAreaIds,
    preferredAreasOptions,
    isLoadingPreferredAreas,
    changePreferredAreas
  ]);

  const {
    input: { onChange: setIsPending }
  } = useField('isPending');

  const {
    input: { onChange: setDepartmentId }
  } = useField('departmentId');
  const {
    input: { value: workDays }
  } = useField('workDays');

  const { employmentTypesOptions } = useEmploymentTypes();
  const { updateEmployee } = useEmployee({ employeeId, enabled: false });
  const resetEmployeeFaceId = useCallback(() => updateEmployee({ accountId, employeeId, faceId: null }), [
    accountId,
    employeeId,
    updateEmployee
  ]);

  useEffect(() => {
    if (addMode && [FIRE_GROUP_ACCOUNT_ID, ARC_ACCOUNT_ID, SETUP_ACCOUNT_ID].includes(accountId)) {
      setIsPending(true);
    }
  }, [accountId, addMode, setIsPending]);

  const minBranchId = Math.min(...(branches || []));
  const selectedBranchId = workDays.findIndex(({ branchId }) => branchId === minBranchId);

  const genderField = useMemo(() => fields.find(({ slug }) => slug === 'gender'), [fields]);
  const levelField = useMemo(() => {
    if (!isSeniorityModelPools({ seniorityModel: accountConfig?.config?.seniorityModel })) return undefined;
    const field = fields.find(({ slug }) => slug === 'level');
    if (!field) return undefined;
    return {
      ...field,
      options: (field.options || []).map((option) => ({
        ...option,
        title: formatLevelOption({ t, level: option.title })
      }))
    };
  }, [fields, accountConfig?.config?.seniorityModel, t]);
  const rankingField = useMemo(() => {
    if (!isSeniorityModelRankingBased({ seniorityModel: accountConfig?.config?.seniorityModel })) return undefined;
    return fields.find(({ slug }) => slug === 'seniority-ranking');
  }, [fields, accountConfig?.config?.seniorityModel]);
  const birthdateField = useMemo(() => fields.find(({ slug }) => slug === 'birthdate'), [fields]);
  const nicknameField = useMemo(() => fields.find(({ slug }) => slug === 'nickname'), [fields]);
  const startEmploymentField = useMemo(() => fields.find(({ slug }) => slug === 'start-employment-date'), [fields]);
  const endEmploymentField = useMemo(() => fields.find(({ slug }) => slug === 'end-employment-date'), [fields]);

  const {
    input: { value: birthdateValue }
  } = useField(`fields.field-${birthdateField?.id}`);

  const employeeTypeField = useMemo(
    () => (accountId === MINT_ACCOUNT_ID ? fields.find(({ id }) => id === MINT_ACCOUNT_EMPLOYEE_TYPE_FIELD_ID) : null),
    [accountId, fields]
  );

  const employeeCompassNiNumber = useMemo(
    () =>
      accountId === COMPASS_DEMO_ACCOUNT_ID ? fields.find(({ id }) => id === COMPASS_DEMO_ACCOUNT_NI_NUMBER_ID) : null,
    [accountId, fields]
  );
  const employeeCompassAka = useMemo(
    () => (accountId === COMPASS_DEMO_ACCOUNT_ID ? fields.find(({ id }) => id === COMPASS_DEMO_ACCOUNT_AKA_ID) : null),
    [accountId, fields]
  );
  const employeeCompassSecondaryAddress = useMemo(
    () =>
      accountId === COMPASS_DEMO_ACCOUNT_ID
        ? fields.find(({ id }) => id === COMPASS_DEMO_ACCOUNT_SECONDARY_ADDRESS_ID)
        : null,
    [accountId, fields]
  );

  const fieldsWithoutExtracted = useMemo(() => {
    const extractedDynamicFieldsArray = fields.filter(({ slug }) => !EXTRACTED_DYNAMIC_FIELDS.includes(slug));

    if (accountId === MINT_ACCOUNT_ID) {
      return extractedDynamicFieldsArray.filter(({ id }) => id !== MINT_ACCOUNT_EMPLOYEE_TYPE_FIELD_ID);
    }
    if (accountId === COMPASS_DEMO_ACCOUNT_ID) {
      return extractedDynamicFieldsArray.filter(
        ({ id }) =>
          id !== COMPASS_DEMO_ACCOUNT_NI_NUMBER_ID &&
          id !== COMPASS_DEMO_ACCOUNT_AKA_ID &&
          id !== COMPASS_DEMO_ACCOUNT_SECONDARY_ADDRESS_ID
      );
    }

    return extractedDynamicFieldsArray;
  }, [accountId, fields]);

  const enableAddNewField = !hasRestrictedFeatures({ accountId, organizationId });

  const {
    input: { value: phone }
  } = useField('phone');
  const {
    input: { value: firstName }
  } = useField('firstName');

  const [selectedField, setSelectedField] = useState();

  const { openUserChat, hasViewChatPrivilege } = useChatIframe();

  const options = useMemo(
    () => [
      ...(hasViewChatPrivilege
        ? [
            {
              label: t('chat'),
              icon: ChatIcon,
              disabled: !userId,
              onClick: () => {
                openUserChat(userId, accountId);
                onClose();
              }
            }
          ]
        : []),
      {
        label: t('whatsapp'),
        disabled: !phone,
        icon: WhatsappIcon,
        onClick: () => window.open(`https://wa.me/${phone}`, '_blank')?.focus()
      },
      {
        label: t('iMessage'),
        disabled: !phone,
        icon: PhoneMenuIconSmall,
        onClick: () => window.open(`sms:${phone}`, '_blank')?.focus()
      },
      {
        label: t('call'),
        disabled: !phone,
        icon: PhoneIcon,
        css: css`
          div > svg > path {
            fill: transparent !important;
          }
        `,
        onClick: () => window.open(`tel:${phone}`, '_blank')?.focus()
      }
    ],
    [accountId, hasViewChatPrivilege, openUserChat, onClose, phone, t, userId]
  );

  const requiredFieldByAccountId = useMemo(() => addMode && MINT_ACCOUNT_ID === accountId, [accountId, addMode]);

  // this is a bug!!
  const overrideRequiredField = useMemo(
    () =>
      !CH_CO_TRAINING_PLATFORM_ACCOUNT_ID && !DELAWARE_NORTH_ID && hasRestrictedFeatures({ accountId, organizationId }),
    [accountId, organizationId]
  );

  const isPhoneOverrideRequiredField = useMemo(() => overrideRequiredField && !DN_PRIVILAGED_ADMINS.includes(adminId), [
    adminId,
    overrideRequiredField
  ]);

  const showAge = birthdateField && birthdateValue;

  const allowedFieldTypes = Object.values(FieldTypes).filter(
    (fieldType) => fieldType !== FieldTypes.FIELD_TYPE_SIGNATURE
  );

  const isBirthdateRequiredByAccountId = accountId === MINT_ACCOUNT_ID || accountId === FNB_ACCOUNT_ID;

  const fieldCount = [birthdateField, showAge, genderField, levelField, rankingField].filter(Boolean).length;

  const rankingFieldProps = useMemo(() => rankingField && { ...rankingField, isRequired: overrideRequiredField }, [
    rankingField,
    overrideRequiredField
  ]);
  const validateRankingField = useCallback(
    (value) => (overrideRequiredField && required(value)) || validRankingField(value),
    [overrideRequiredField]
  );

  return (
    <RootWrapper>
      {isLoading && <Loader />}

      <RootContainer isLoading={isLoading}>
        <AutoCompleteOff />

        {!addMode && employeeId && (
          <MobileKpis>
            <EmployeeKpis employeeId={employeeId} firstName={firstName} />
          </MobileKpis>
        )}

        <Accordion defaultOpenRows={{ 0: true }} sticky>
          <Accordion.Item title={t('generalDetails')}>
            <AccordionContent>
              <FieldsContainer $count={2}>
                <InputField
                  name="firstName"
                  title={t('firstName')}
                  dataTestId="employee-first-name-input"
                  validate={(value, falseInclude) =>
                    validateNameValue({
                      value,
                      falseInclude,
                      isRequired: true,
                      maxLength: MAX_FIRST_LAST_NAME_LENGTH
                    })
                  }
                  metadata={{ maxNumberOfChar: MAX_FIRST_LAST_NAME_LENGTH }}
                  onChange={startsWithCapital}
                  autoFocus
                  required
                  style={{ width: '100%' }}
                />
                <InputField
                  name="lastName"
                  title={t('lastName')}
                  dataTestId="employee-last-name-input"
                  metadata={{ maxNumberOfChar: MAX_FIRST_LAST_NAME_LENGTH }}
                  onChange={startsWithCapital}
                  required={overrideRequiredField}
                  validate={(value, falseInclude) =>
                    validateNameValue({
                      value,
                      falseInclude,
                      isRequired: overrideRequiredField,
                      maxLength: MAX_FIRST_LAST_NAME_LENGTH
                    })
                  }
                  style={{ width: '100%' }}
                />
              </FieldsContainer>
              <FieldsContainer $count={2}>
                {account?.createUserBy !== CREATE_USER_BY.EMAIL && (
                  <PhoneField
                    name="phone"
                    title={t('phone')}
                    type="phone"
                    dataTestId="employee-phone-input"
                    validate={isPhoneOverrideRequiredField ? required : undefined}
                    required={isPhoneOverrideRequiredField}
                    autoFocus={false}>
                    <MenuContainer isRtl={isRtl}>
                      <ContextMenu options={options} usePortal>
                        <PhoneMenuIcon />
                      </ContextMenu>
                    </MenuContainer>
                  </PhoneField>
                )}
                <EmployeeIdInputField
                  name="employeeIdField"
                  title={t('employeeId')}
                  isDisabled={disableEmployeeIdField && !isUbeyaAdmin}
                  readOnly={accountId === ISRAEL_647_ACCOUNT_ID}
                  required={overrideRequiredField}
                  validate={overrideRequiredField ? required : undefined}
                />
              </FieldsContainer>
              <FieldsContainer $count={2}>
                <PhoneField name="phoneSecondary" title={t('secondaryPhone')} type="phone" autoFocus={false} />

                <InputField
                  name="email"
                  title={t('email')}
                  type="email"
                  dataTestId="employee-email-input"
                  validate={(value, falseInclude) => {
                    const requiredValue = required(value, falseInclude);
                    if ((requiredFieldByAccountId || overrideRequiredField) && requiredValue) {
                      return requiredValue;
                    }
                    return validEmail(value);
                  }}
                  required={requiredFieldByAccountId || overrideRequiredField}
                />
              </FieldsContainer>
              {branchesOptions.length > 1 && (
                <FieldsContainer $count={1}>
                  <SelectField
                    name="branches"
                    title={t('branches')}
                    dataTestId="employee-branches-select"
                    validate={required}
                    options={groupedBranchesOptions}
                    onChange={handleBranchesChange}
                    getOptionSpecificStyle={getBranchOptionStyle}
                    filterFunc={branchFilterFunc}
                    required
                  />
                </FieldsContainer>
              )}
              {nicknameField && (
                <FieldsContainer $count={1}>
                  <DynamicFieldComp
                    title={<ExplanationTooltip title={t('nickname')} body={t('nicknameTooltip')} />}
                    field={nicknameField}
                  />
                </FieldsContainer>
              )}
              <FieldsContainer $count={1}>
                <Notes name="notes" title={t('notes')} isMultiline />
              </FieldsContainer>
              {birthdateField || genderField || levelField || rankingField ? (
                <FieldsContainer $count={fieldCount}>
                  {birthdateField && (
                    <DynamicFieldComp
                      field={{
                        ...birthdateField,
                        isRequired: isBirthdateRequiredByAccountId || overrideRequiredField
                      }}
                      validate={(value) =>
                        validBirthdate(value, isBirthdateRequiredByAccountId || overrideRequiredField)
                      }
                      overrideRequiredField={overrideRequiredField}
                    />
                  )}

                  {showAge && (
                    <AgeText
                      title={t('age')}
                      value={birthdateValue ? moment().diff(moment(birthdateValue, API_DATE_TIME_FORMAT), 'years') : ''}
                      disabled
                    />
                  )}
                  {genderField && (
                    <DynamicFieldComp
                      field={{
                        ...genderField,
                        isRequired: overrideRequiredField
                      }}
                      validate={(value) => (overrideRequiredField ? required(value) : undefined)}
                      overrideRequiredField={overrideRequiredField}
                    />
                  )}
                  {levelField && (
                    <DynamicFieldComp
                      field={{
                        ...levelField,
                        isRequired: overrideRequiredField
                      }}
                      validate={(value) => (overrideRequiredField ? required(value) : undefined)}
                      overrideRequiredField={overrideRequiredField}
                    />
                  )}
                  {rankingField && (
                    <DynamicFieldComp
                      field={rankingFieldProps}
                      validate={validateRankingField}
                      overrideRequiredField={overrideRequiredField}
                    />
                  )}
                </FieldsContainer>
              ) : null}

              <FieldsContainer $count={1}>
                <ToggleField className="span-3" name="isPending" text={t('pendingApproval')} />
              </FieldsContainer>
            </AccordionContent>
          </Accordion.Item>
        </Accordion>

        <Accordion defaultOpenRows={{ 0: true }} sticky>
          <Accordion.Item title={t('employmentDetails')}>
            <AccordionContent>
              <FieldsContainer $count={1}>
                <PositionsSelectField
                  name="positions"
                  title={t('positions')}
                  dataTestId="employee-positions-select"
                  isMulti
                  onAdd={(newId) => changePositions([...positions, newId])}
                  includePositionGroups
                  customPositionsOptions={positionsOptionsWithDisabled}
                />
              </FieldsContainer>

              {hasPositions && enableContractLeaveManagementFeature && (
                <FieldsContainer $count={1}>
                  <SelectField
                    name="primaryPositionIds"
                    title={t('primaryPositions')}
                    dataTestId="employee-primary-positions-select"
                    isMulti
                    options={primaryPositionsOptions}
                    usePortal
                  />
                </FieldsContainer>
              )}
              <FieldsContainer $count={1}>
                <PreferredClientsSelectField name="preferredClients" title={t('preferredClients')} isMulti />
              </FieldsContainer>
              <FieldsContainer $count={1}>
                <BlockedClientsSelectField name="blockedClients" title={t('blockedClients')} isMulti />
              </FieldsContainer>
              {isEmployeePreferredAreasEnabled && !addMode && (
                <FieldsContainer $count={1}>
                  <SelectField
                    name="preferredAreaIds"
                    title={t('preferredAreas')}
                    dataTestId="employee-preferred-areas-select"
                    isMulti
                    options={preferredAreasOptions}
                    usePortal
                  />
                </FieldsContainer>
              )}
              <FieldsContainer $count={enableContractFeature ? 1 : 2}>
                <DepartmentSelectField onAdd={setDepartmentId} onClear={() => setDepartmentId(null)} clearable />
                {!enableContractFeature && (
                  <SelectField
                    name="employmentTypeId"
                    title={t('employmentType')}
                    options={employmentTypesOptions}
                    isMulti={false}
                  />
                )}
              </FieldsContainer>
              <FieldsContainer $count={2}>
                {startEmploymentField && (
                  <DynamicFieldComp field={startEmploymentField} overrideRequiredField={overrideRequiredField} />
                )}
                {endEmploymentField && (
                  <DynamicFieldComp field={endEmploymentField} overrideRequiredField={overrideRequiredField} />
                )}
              </FieldsContainer>
              <FieldsContainer $count={1}>
                <ToggleField
                  name="isDefaultCaptain"
                  text={<ExplanationTooltip title={t('isDefaultCaptain')} body={t('tooltipDefaultCaptain')} />}
                />
              </FieldsContainer>
              <FieldsContainer $count={2}>
                <ComposeFields title={t('type')} hint={t('mustSelectAtLeastOne')}>
                  <CheckBoxField
                    name="isShift"
                    title={<ExplanationTooltip title={t('shift')} body={t('tooltipShiftEmployee')} />}
                  />
                  <CheckBoxField
                    name="isDaily"
                    title={<ExplanationTooltip title={t('daily')} body={t('tooltipDailyEmployee')} />}
                  />
                </ComposeFields>

                {isDaily && <DaysFields name={`workDays[${selectedBranchId}].days`} title={t('workDays')} />}
              </FieldsContainer>
              {employeeCompassAka || employeeCompassNiNumber || employeeCompassSecondaryAddress || faceId ? (
                <FieldsContainer $count={4}>
                  {employeeCompassAka && (
                    <DynamicFieldComp
                      field={employeeCompassAka}
                      className="span-2"
                      overrideRequiredField={overrideRequiredField}
                    />
                  )}
                  {employeeCompassNiNumber && (
                    <DynamicFieldComp
                      field={employeeCompassNiNumber}
                      className="span-2"
                      overrideRequiredField={overrideRequiredField}
                    />
                  )}
                  {employeeCompassSecondaryAddress && (
                    <DynamicFieldComp
                      field={employeeCompassSecondaryAddress}
                      className="span-2"
                      overrideRequiredField={overrideRequiredField}
                    />
                  )}
                  {faceId && <Button onClick={resetEmployeeFaceId}>{t('deleteFaceId')}</Button>}
                </FieldsContainer>
              ) : null}
            </AccordionContent>
          </Accordion.Item>
        </Accordion>

        {ALPHA_ACCOUNT_ID === accountId && (
          <Accordion defaultOpenRows={{ 0: true }} sticky>
            <Accordion.Item title={t('PTO')}>
              <AccordionContent>
                <FieldsContainer $count={2}>
                  <TextInput title={t('yearlyAmountOfTime')} type="number" placeholder="25" />
                  <AccuredText>Accrued PTO days: 10 </AccuredText>
                </FieldsContainer>
              </AccordionContent>
            </Accordion.Item>
          </Accordion>
        )}

        <Accordion defaultOpenRows={{ 0: true }} sticky>
          <Accordion.Item
            title={<ExplanationTooltip title={t('employeeSectionOther')} body={t('tooltipOtherFields')} />}>
            <AccordionContent>
              <FieldsContainer $shouldBrakeLabels>
                {fieldsWithoutExtracted.map((field) => {
                  const isRequiredMadridField =
                    accountId === MADRID_LEGENDS_ACCOUNT_ID &&
                    field.id === MADRID_LEGENDS_CONTRACTED_BASE_RATE_FIELD_ID;

                  return renderDynamicFieldOrMask({
                    baseField: field,
                    props: {
                      field: isRequiredMadridField ? { ...field, isRequired: true } : field,
                      ...(isRequiredMadridField ? { validate: required } : {}),
                      overrideRequiredField: isRequiredMadridField || overrideRequiredField,
                      enableShowLinks: accountId === ONB_ACCOUNT_ID
                    }
                  });
                })}
                {employeeTypeField &&
                  renderDynamicFieldOrMask({
                    baseField: employeeTypeField,
                    props: {
                      field: { ...employeeTypeField, requiredField: true },
                      validate: required,
                      overrideRequiredField
                    }
                  })}
                {enableAddNewField && (
                  <div>
                    <Button secondary onClick={() => setSelectedField({})}>
                      {t('addField')}
                    </Button>
                  </div>
                )}
              </FieldsContainer>
            </AccordionContent>
          </Accordion.Item>
        </Accordion>

        {isUbeyaAdmin && !addMode && (
          <Accordion defaultOpenRows={{ 0: true }} sticky>
            <Accordion.Item title="(visible only for Ubeya admins)">
              <AccordionContent>
                <FieldsContainer $count={6}>
                  <TextInput
                    title="Ubeya Employee ID"
                    type="text"
                    value={employeeId || ''}
                    disabled
                    className="span-2"
                  />
                  <TextInput title="User Id" type="text" value={userId || ''} disabled className="span-2" />
                  <TextInput
                    title="App Version"
                    type="text"
                    value={userDevice?.appVersion || ''}
                    disabled
                    className="span-2"
                  />
                  <TextInput
                    title="Platform"
                    type="text"
                    value={`${userDevice?.platform || ''}`}
                    disabled
                    className="span-2"
                  />
                  <TextInput
                    title="Platform Version"
                    value={userDevice?.platformVersion || ''}
                    disabled
                    className="span-2"
                  />
                </FieldsContainer>
              </AccordionContent>
            </Accordion.Item>
          </Accordion>
        )}
      </RootContainer>

      {selectedField && (
        <FieldModal
          selectedField={selectedField}
          onClose={() => setSelectedField()}
          allowedFieldTypes={allowedFieldTypes}
          showEmployeeSensitiveOption
          onConfirm={(field) => addField({ ...field, priority: fields.length + 1 })}
        />
      )}
    </RootWrapper>
  );
};

export default React.memo(Profile);
